<template>
	<div class="dynamic-charts-wrapper">
		<!-- Shared full-name popup (bar y-axis labels + pie legend labels) -->
		<div v-if="labelTooltip.visible" class="label-tooltip" :style="{ left: labelTooltip.x + 'px', top: labelTooltip.y + 'px' }">
			<div v-for="(line, idx) in labelTooltip.lines" :key="idx">{{ line }}</div>
		</div>

		<!-- Chart Header -->
		<div class="text-center">
			<h4 class="font-semibold text-gray-800" style="text-wrap: balance">
				{{ props.title || props.emissionFactors.emissionFactorName || "Chart" }}
			</h4>
		</div>

		<!-- No Data Message -->
		<div v-if="hasNoData" class="flex items-center justify-center h-full min-h-[300px]">
			<div class="text-center p-8 bg-gray-100 rounded-lg">
				<i class="pi pi-chart-line text-6xl text-gray-300 mb-4"></i>
				<p class="text-xl font-semibold text-gray-500">No Data Available</p>
				<p class="text-sm text-gray-400 mt-2">All values are zero or empty</p>
			</div>
		</div>

		<!-- Bar Chart -->
		<div v-else-if="!showPieChart" class="mt-2 mb-2" @mousemove="onBarMouseMove" @mouseleave="labelTooltip.visible = false">
			<!-- Scrollable mode: fixed height + sticky x-axis -->
			<template v-if="scrollMode">
				<div class="chart-scroll-body">
					<div :style="{ height: chartHeight + 'px' }">
						<Chart
							class="w-full h-full"
							type="bar"
							:data="barChartData"
							:options="scrollableBarsOptions"
							chart-id="dynamic-bar-chart"
							dataset-id-key="label"
						/>
					</div>
				</div>
				<div style="height: 50px; overflow: hidden">
					<Chart
						class="w-full h-full"
						type="bar"
						:data="xAxisChartData"
						:options="xAxisOnlyOptions"
						chart-id="dynamic-bar-chart-xaxis"
						dataset-id-key="label"
					/>
				</div>
			</template>

			<!-- Full height mode: no scroll -->
			<template v-else>
				<div :style="{ height: chartHeight + 'px' }">
					<Chart
						class="w-full h-full"
						type="bar"
						:data="barChartData"
						:options="fullHeightBarsOptions"
						chart-id="dynamic-bar-chart"
						dataset-id-key="label"
					/>
				</div>
			</template>
		</div>

		<!-- Pie Chart -->
		<div v-else class="flex mt-2 mb-2" style="height: 400px">
			<Chart class="w-full h-full" type="pie" :data="pieChartData" :options="pieChartOptions" chart-id="dynamic-pie-chart" dataset-id-key="label" />
		</div>
	</div>
</template>

<script setup lang="ts">
import { valueToStringFormatter } from "@/composables/resultGridHelper"
import { computed, ref } from "vue"
import { EmissionFactors } from "../../entities/NSwagGenerated"
import { generateCategoricalChartColors } from "../composables/chartColorHelper"
import Chart from "primevue/chart"
// Chart.js class — used to resolve the live chart instance from a canvas so we
// can hit-test the y-axis label gutter on native mousemove (onHover does not
// reliably fire over the axis labels).
import { Chart as ChartJS } from "chart.js"

// Interface for chart data items
export interface ChartDataItem {
	name: string
	emissionFactors: { [key: string]: number }
}

interface Props {
	showPieChart: boolean
	scrollMode: boolean
	emissionFactors: EmissionFactors
	chartData: ChartDataItem[]
	title?: string
}

const props = defineProps<Props>()

const Y_AXIS_WIDTH = 150
const SCROLL_CONTAINER_HEIGHT = 400

const sortedChartData = computed(() => {
	const efName = props.emissionFactors.emissionFactorName
	return [...props.chartData].sort((a, b) => {
		const valA = efName ? (a.emissionFactors[efName] ?? 0) : 0
		const valB = efName ? (b.emissionFactors[efName] ?? 0) : 0
		return valB - valA
	})
})

const graphLabels = computed(() => sortedChartData.value.map((item) => item.name))

const MAX_LABEL_LINES = 4
const LABEL_WRAP_WIDTH = 20 // narrow: fits the fixed 150px y-axis gutter
const TOOLTIP_WRAP_WIDTH = 34 // wider: tooltips/popups have room to breathe
const LEGEND_LABEL_MAX = 28

// Hard-break a token with no spaces (e.g. a long chemical name) into chunks.
function hardBreak(text: string, max: number): string[] {
	const out: string[] = []
	for (let i = 0; i < text.length; i += max) out.push(text.slice(i, i + max))
	return out
}

// Word-wrap a chunk to roughly `max` characters, hard-breaking over-long words.
function wrapByWidth(text: string, max: number): string[] {
	const trimmed = text.trim()
	if (trimmed.length <= max) return trimmed ? [trimmed] : []
	const words = trimmed.split(" ")
	const lines: string[] = []
	let current = ""
	for (const word of words) {
		if ((current + word).length > max && current.length > 0) {
			lines.push(current.trim())
			current = word + " "
		} else {
			current += word + " "
		}
	}
	if (current.trim().length > 0) lines.push(current.trim())
	return lines.flatMap((l) => (l.length > max ? hardBreak(l, max) : [l]))
}

// Full multi-line split of a label, WITHOUT truncation. Preference order:
//   1. break right after every closing parenthesis ")" (chemical names),
//   2. else break at "/" (path-like labels),
//   3. else word-wrap by width.
// Used by tooltips/popups that must show the whole name.
function splitLabelFull(label: string, width: number = LABEL_WRAP_WIDTH): string[] {
	if (!label) return [""]
	if (label.includes(")")) {
		const parts = label.match(/[^)]*\)|[^)]+$/g)?.map((p) => p.trim()).filter(Boolean) ?? [label]
		return parts.flatMap((p) => wrapByWidth(p, width))
	}
	if (label.includes("/")) {
		return label.split("/").map((p) => p.trim())
	}
	return wrapByWidth(label, width)
}

// Truncated version for the axis tick: at most MAX_LABEL_LINES lines, with an
// ellipsis appended to the last visible line when the name is longer.
function wrapLabel(label: string): string[] {
	const lines = splitLabelFull(label)
	if (lines.length <= MAX_LABEL_LINES) return lines
	const truncated = lines.slice(0, MAX_LABEL_LINES)
	truncated[MAX_LABEL_LINES - 1] = truncated[MAX_LABEL_LINES - 1] + " …"
	return truncated
}

const maxLabelLines = computed(() => {
	let max = 1
	for (const l of graphLabels.value) {
		const n = wrapLabel(l).length
		if (n > max) max = n
	}
	return max
})

// Each row needs enough vertical space for its (wrapped) label not to collide
// with neighbours. Tick font-size is 11px, with ~1.4 line-height ≈ 16px per
// line. Add ~24px padding so short labels still get a comfortable bar.
const chartHeight = computed(() => {
	const perRow = Math.max(60, maxLabelLines.value * 16 + 24)
	return Math.max(SCROLL_CONTAINER_HEIGHT, graphLabels.value.length * perRow)
})

// Y-axis label hover popup: shows the full (untruncated) material name when the
// cursor is over a (possibly truncated) tick label on the bar chart.
const labelTooltip = ref({ visible: false, x: 0, y: 0, lines: [] as string[] })

// Native mousemove on the bar-chart wrapper. Resolves the live Chart.js instance
// from the canvas and, when the cursor is over the y-axis label gutter, shows the
// full material name in labelTooltip. Uses a DOM listener because Chart.js
// onHover does not fire reliably over the axis labels.
// Enable/disable a chart's built-in tooltip, re-rendering only when the state
// actually changes (avoids per-frame flicker).
function setChartTooltipEnabled(chart: any, enabled: boolean) {
	const tip = chart?.options?.plugins?.tooltip
	if (!tip || tip.enabled === enabled) return
	tip.enabled = enabled
	try {
		if (!enabled) {
			chart.setActiveElements([])
			if (chart.tooltip) chart.tooltip.setActiveElements([], { x: 0, y: 0 })
		}
		chart.update("none")
	} catch {
		/* best-effort */
	}
}

function onBarMouseMove(e: MouseEvent) {
	const wrapper = e.currentTarget as HTMLElement
	const canvas = wrapper.querySelector("canvas") as HTMLCanvasElement | null
	if (!canvas) {
		labelTooltip.value.visible = false
		return
	}
	const chart: any = ChartJS.getChart(canvas)
	if (!chart?.chartArea || !chart?.scales?.y) {
		labelTooltip.value.visible = false
		return
	}
	const rect = canvas.getBoundingClientRect()
	const x = e.clientX - rect.left
	const y = e.clientY - rect.top

	const inGutter = x <= chart.chartArea.left && y >= chart.chartArea.top && y <= chart.chartArea.bottom
	const idx = inGutter ? Math.round(chart.scales.y.getValueForPixel(y)) : -1
	const label = idx >= 0 ? graphLabels.value[idx] : ""

	if (!inGutter || !label) {
		// On the plot / outside the gutter: hide our popup, restore the bar tooltip.
		labelTooltip.value.visible = false
		setChartTooltipEnabled(chart, true)
		return
	}

	// Over a label: show the full-name popup and suppress the bar tooltip so the
	// two don't overlap.
	setChartTooltipEnabled(chart, false)
	labelTooltip.value = {
		visible: true,
		x: e.clientX + 14,
		y: e.clientY - 10,
		lines: splitLabelFull(label, TOOLTIP_WRAP_WIDTH),
	}
}

const barGraphDatasetData = computed(() =>
	sortedChartData.value.map((item) =>
		props.emissionFactors.emissionFactorName ? (item.emissionFactors[props.emissionFactors.emissionFactorName] ?? 0) : 0
	)
)

// Check if all data values are null, undefined, or 0
const hasNoData = computed(() => {
	const data = barGraphDatasetData.value
	if (!data || data.length === 0) return true
	return data.every((value) => value === null || value === undefined || value === 0)
})

const chartSliceColors = computed(() => generateCategoricalChartColors(graphLabels.value))

// Chart data computed properties
const commonGraphData = computed(() => ({
	labels: graphLabels.value,
	datasets: [
		{
			label: "", // Remove label since we have header
			backgroundColor: chartSliceColors.value,
			data: barGraphDatasetData.value,
		},
	],
}))

const barChartData = computed(() => ({
	...commonGraphData.value,
	datasets: commonGraphData.value.datasets.map((dataset) => ({
		...dataset,
		borderColor: "#1988E5",
	})),
}))

// X-axis chart uses same data but with transparent bars so Chart.js computes the same scale range
const xAxisChartData = computed(() => ({
	labels: graphLabels.value,
	datasets: [
		{
			label: "",
			backgroundColor: "transparent",
			borderColor: "transparent",
			data: barGraphDatasetData.value,
		},
	],
}))

const pieChartData = computed(() => ({
	...commonGraphData.value,
	datasets: commonGraphData.value.datasets.map((dataset) => ({
		...dataset,
		borderColor: "#fff",
	})),
}))

// Bar chart without x-axis (scrollable section)
const scrollableBarsOptions = {
	indexAxis: "y",
	maintainAspectRatio: false,
	responsive: true,
	animation: false,
	layout: {
		padding: { left: 10, right: 20, top: 10, bottom: 0 },
	},
	scales: {
		x: {
			beginAtZero: true,
			ticks: { display: false },
			border: { display: false },
			grid: { display: true, color: "rgba(0, 0, 0, 0.1)" },
		},
		y: {
			beginAtZero: true,
			afterFit: (scale: any) => {
				scale.width = Y_AXIS_WIDTH
			},
			ticks: {
				// Chart.js by default auto-skips alternate ticks when it thinks
				// labels would overlap — which is what produces the "1 yes 1 no"
				// pattern. We word-wrap manually in the callback, so disable.
				autoSkip: false,
				maxRotation: 0,
				minRotation: 0,
				font: { size: 11 },
				callback: function (value: any, index: number): string | string[] {
					return wrapLabel(graphLabels.value[index] || "")
				},
			},
			grid: { display: true, color: "rgba(0, 0, 0, 0.1)" },
		},
	},
	plugins: {
		legend: { display: false },
		// Tooltip on the bar shows the full (untruncated) name, wrapped at ")".
		tooltip: {
			callbacks: {
				title: (items: any[]) => (items.length ? splitLabelFull(graphLabels.value[items[0].dataIndex] || "", TOOLTIP_WRAP_WIDTH) : ""),
			},
		},
	},
}

// Fixed x-axis strip: only the x-axis, no bars, left-padded to align with y-axis above
const xAxisOnlyOptions = {
	indexAxis: "y",
	maintainAspectRatio: false,
	responsive: true,
	animation: false,
	layout: {
		padding: { left: 10, right: 20, top: 0, bottom: 0 },
	},
	scales: {
		x: {
			display: true,
			beginAtZero: true,
			grid: { display: false },
		},
		y: {
			display: false,
			afterFit: (scale: any) => {
				scale.width = Y_AXIS_WIDTH
			},
		},
	},
	plugins: { legend: { display: false } },
}

// Full height bar chart (scroll off): same as scrollableBarsOptions but x-axis visible
const fullHeightBarsOptions = {
	indexAxis: "y",
	maintainAspectRatio: false,
	responsive: true,
	animation: false,
	layout: {
		padding: { left: 10, right: 20, top: 10, bottom: 10 },
	},
	scales: {
		x: {
			display: true,
			beginAtZero: true,
		},
		y: {
			beginAtZero: true,
			afterFit: (scale: any) => {
				scale.width = Y_AXIS_WIDTH
			},
			ticks: {
				autoSkip: false,
				maxRotation: 0,
				minRotation: 0,
				font: { size: 11 },
				callback: function (value: any, index: number): string | string[] {
					return wrapLabel(graphLabels.value[index] || "")
				},
			},
			grid: { display: true, color: "rgba(0, 0, 0, 0.1)" },
		},
	},
	plugins: {
		legend: { display: false },
		tooltip: {
			callbacks: {
				title: (items: any[]) => (items.length ? splitLabelFull(graphLabels.value[items[0].dataIndex] || "", TOOLTIP_WRAP_WIDTH) : ""),
			},
		},
	},
}

const pieChartOptions = computed(() => ({
	responsive: true,
	maintainAspectRatio: false,
	plugins: {
		legend: {
			display: true,
			position: "bottom",

			onHover: function (event: any, legendItem: any, legend: any) {
				const fullLabel = legend.chart.data.labels[legendItem.index] || ""
				if (!fullLabel) return
				// Show the full (untruncated) name for any legend item, wrapped at ")".
				labelTooltip.value = {
					visible: true,
					x: event.native.clientX + 14,
					y: event.native.clientY - 10,
					lines: splitLabelFull(fullLabel, TOOLTIP_WRAP_WIDTH),
				}
			},
			onLeave: function () {
				labelTooltip.value.visible = false
			},
			labels: {
				padding: 20,
				usePointStyle: true,
				generateLabels: function (chart: any) {
					const datasets = chart.data.datasets
					const labels = chart.data.labels || []
					if (!datasets.length) return []
					return labels.map((label: string, i: number) => {
						const meta = chart.getDatasetMeta(0)
						const style = meta.controller.getStyle(i)
						// Short legend text: last path segment for "/" labels, otherwise
						// the name truncated with an ellipsis. Full name shown on hover.
						const base = label.includes("/") ? label.split("/").pop()!.trim() : label
						const shortLabel = base.length > LEGEND_LABEL_MAX ? base.slice(0, LEGEND_LABEL_MAX).trimEnd() + " …" : base
						return {
							text: shortLabel,
							fillStyle: style.backgroundColor,
							strokeStyle: style.borderColor,
							lineWidth: style.borderWidth,
							pointStyle: "circle",
							hidden: !chart.getDataVisibility(i),
							index: i,
						}
					})
				},
			},
		},
		tooltip: {
			callbacks: {
				title: function (tooltipItems: any[]) {
					if (!tooltipItems.length) return ""
					// Full name, wrapped at ")" — never truncated in the tooltip.
					return splitLabelFull(tooltipItems[0].label || "", TOOLTIP_WRAP_WIDTH)
				},
				label: function (context: any) {
					const value = context.raw || 0
					const total = context.dataset.data.reduce((sum: number, val: number) => sum + val, 0)
					const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : "0"
					const formattedValue = typeof value === "number" ? valueToStringFormatter(value) : value
					return `${formattedValue} (${percentage}%)`
				},
			},
		},
	},
}))
</script>

<style scoped>
.dynamic-charts-wrapper {
	width: 100%;
}

.chart-scroll-body {
	height: 400px;
	overflow-y: auto;
	overflow-x: hidden;
}
/* Full-name popup for truncated y-axis labels. Fixed positioning keyed off the
   cursor (clientX/clientY) so it works regardless of ancestor positioning. */
.label-tooltip {
	position: fixed;
	background: rgba(0, 0, 0, 0.85);
	color: #fff;
	padding: 6px 10px;
	border-radius: 4px;
	font-size: 12px;
	line-height: 1.4;
	pointer-events: none;
	z-index: 50;
	max-width: 460px;
	white-space: nowrap;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}
</style>
